import {ChangeEventHandler, Dispatch, MouseEventHandler, SetStateAction, useCallback, useEffect} from 'react';
import Router, {useRouter} from 'next/router';
import {MIN_FETCH_SYMBOLS_AMOUNT} from 'modules/search-popup/constants';
import {checkIsSpace} from 'core/regex';
import {getSearchPageLink} from 'modules/search-page-new/helpers';
import {useSearchGtagEvents} from 'hooks/analytics/gtag/search/useSearchGtagEvents';

const MAX_HISTORY_ITEMS = 4;

export function useSearchModule(
    searchValue: string,
    setSearchValue: Dispatch<SetStateAction<string>>,
    setIsPanelVisible?: (visibility: boolean) => void,
    onClose?: () => void,
) {
    const {handleSearchClickGtag} = useSearchGtagEvents();
    const router = useRouter();

    const openPanel = useCallback(() => {
        setIsPanelVisible?.(true);
    }, [setIsPanelVisible]);

    const closePanel = useCallback(() => {
        setIsPanelVisible?.(false);
    }, [setIsPanelVisible]);

    const handleSearchClick = useCallback(() => {
        handleSearchClickGtag();
        openPanel();
    }, [handleSearchClickGtag, openPanel]);

    const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        ({target}) => {
            const value = target.value;

            openPanel();

            if (!checkIsSpace(value)) {
                setSearchValue(value);
            }
        },
        [openPanel, setSearchValue],
    );

    const handleClose = useCallback<MouseEventHandler<HTMLButtonElement>>(
        (event) => {
            event.preventDefault();
            onClose?.();
        },
        [onClose],
    );

    const saveToHistory = (value: string) => {
        if ('undefined' === typeof window) {
            return;
        }

        const raw = localStorage.getItem('recently');
        const history: string[] = raw ? JSON.parse(raw) : [];

        if (history.includes(value)) {
            return;
        }

        const updated = [value, ...history].slice(0, MAX_HISTORY_ITEMS);
        localStorage.setItem('recently', JSON.stringify(updated));
    };

    const search = useCallback(
        (value: string) => {
            saveToHistory(value);
            router.push(getSearchPageLink(value));
        },
        [router],
    );

    const handleSubmit = useCallback(
        (placeholder?: string) => () => {
            const value = searchValue || placeholder;
            if (!value) {
                return;
            }

            if (value.length < MIN_FETCH_SYMBOLS_AMOUNT) {
                return;
            }

            if (!searchValue && placeholder) {
                setSearchValue(placeholder);
            }

            search(value);
        },
        [search, searchValue, setSearchValue],
    );

    const handleCrossClick = useCallback(() => {
        setSearchValue('');
        closePanel();
    }, [setSearchValue, closePanel]);

    useEffect(() => {
        const handleRouteChangeStart = () => {
            closePanel();
            onClose?.();
            setSearchValue('');
        };

        Router.events.on('routeChangeStart', handleRouteChangeStart);

        return () => {
            Router.events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, [closePanel, onClose, setSearchValue]);

    return {
        handleClose,
        handleCrossClick,
        handlePanelClose: closePanel,
        handleSearchClick,
        handleSubmit,
        onChange,
    };
}
